import type { XHRInstance } from "@sembark-travel/xhr"

export type TEmailAddress = { email: string; name: string | null }

export type TComposeMailbox = { id: number; name: string; email: string }

/** A record an email can be about. */
export type TComposableModelType = "trips" | "trip_plan_requests"

/**
 * A thread an email can reply on: it goes out from the thread's mailbox,
 * answering `reply_to_message_id`, with that reply's To / CC / subject.
 */
export type TComposeThread = {
	id: number
	subject: string | null
	latest_message_at: string | null
	mailbox: TComposeMailbox
	reply_to_message_id: number | null
	reply_subject: string
	to: Array<TEmailAddress>
	cc: Array<TEmailAddress>
}

/** Starting a new thread about a record. */
export type TComposeNewThread = {
	mailbox: TComposeMailbox
	mailboxes: Array<TComposeMailbox>
	to: Array<TEmailAddress>
	cc: Array<TEmailAddress>
}

/**
 * What the email composer starts from: the threads the user can reply on
 * (`mail_thread_id` picked, null for a new one) and, about a record, a new
 * thread.
 */
export type TMailComposeDefaults = {
	threads: Array<TComposeThread>
	mail_thread_id: number | null
	new: TComposeNewThread | null
	has_email_signature: boolean
	max_attachment_bytes: number
}

type TMailMessagePayload = {
	to: string
	cc: string
	subject: string
	body: string
	include_signature: 0 | 1
	attachments: Array<File>
}

/** A reply on a thread, quoting the message it answers unless turned off. */
export type TThreadReplyPayload = TMailMessagePayload & {
	mail_thread_id: number
	reply_to_message_id: number | null
	include_original: 0 | 1
	/** Browser convention (UTC minus local, in minutes), for the quote's date. */
	timezone_offset: number
}

/** A new thread about a record. */
export type TNewThreadPayload = TMailMessagePayload & {
	model_type: TComposableModelType
	model_id: number
	mailbox_id: number
}

export function fetchComposeDefaults(
	xhr: XHRInstance,
	params:
		| { message_id: number }
		| { model_type: TComposableModelType; model_id: number }
): Promise<TMailComposeDefaults> {
	return xhr
		.get("/mail-messages/compose", { params })
		.then((resp) => resp.data.data)
}

/** Sent as multipart, so the attachments upload with the email. */
export function postMailMessage(
	xhr: XHRInstance,
	{ attachments, ...fields }: TThreadReplyPayload | TNewThreadPayload
) {
	const data = new FormData()
	for (const [name, value] of Object.entries(fields)) {
		if (value !== null && value !== undefined) {
			data.append(name, String(value))
		}
	}
	for (const file of attachments) {
		data.append("attachments[]", file)
	}
	return xhr.post("/mail-messages", data)
}

export function formatBytes(bytes: number): string {
	const mb = bytes / (1024 * 1024)
	if (mb >= 1) {
		return `${Math.round(mb * 10) / 10} MB`
	}
	return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

export function joinEmails(addresses: Array<TEmailAddress>): string {
	return addresses.map((address) => address.email).join(", ")
}
